import React from 'react';
import { Link } from 'react-router-dom';
import {
    Anchor,
    Shield,
    Radio,
    Sword,
    Server,
    Truck,
    ArrowRight,
    CheckCircle2,
    Target,
    Zap,
    Clock,
    Users
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernMarineCorps = () => {
    const products = [
        {
            icon: <Radio size={28} />,
            title: 'Expeditionary Communications',
            description: 'Rapidly deployable communication systems for expeditionary operations.',
            badge: 'Field-proven',
            features: ['Quick deploy', 'Portable systems', 'Secure voice/data', 'SATCOM capable']
        },
        {
            icon: <Sword size={28} />,
            title: 'Combat Systems Integration',
            description: 'Integrated systems for Marine ground combat operations.',
            badge: 'Combat-ready',
            features: ['Fire control', 'Situational awareness', 'Target acquisition', 'C2 systems']
        },
        {
            icon: <Truck size={28} />,
            title: 'Tactical Vehicle Technology',
            description: 'IT systems for tactical and logistics vehicles.',
            badge: 'Ruggedized',
            features: ['Vehicle networks', 'GPS tracking', 'Diagnostics', 'Communication suites']
        },
        {
            icon: <Server size={28} />,
            title: 'Forward Operating Base IT',
            description: 'Complete IT infrastructure for FOBs and expeditionary sites.',
            badge: 'Deployable',
            features: ['Modular design', 'Power efficient', 'Quick setup', 'Secure networking']
        }
    ];

    const capabilities = [
        {
            icon: <Target size={28} />,
            metric: '<2hrs',
            title: 'Rapid Deployment',
            description: 'Full system operational in under 2 hours'
        },
        {
            icon: <Shield size={28} />,
            metric: 'MIL-STD',
            title: 'Ruggedized',
            description: 'Built for the harshest environments'
        },
        {
            icon: <Zap size={28} />,
            metric: '24/7',
            title: 'Support',
            description: 'Round-the-clock expeditionary support'
        },
        {
            icon: <Clock size={28} />,
            metric: '99.7%',
            title: 'Reliability',
            description: 'Proven in combat operations'
        }
    ];

    const features = [
        { icon: <Anchor size={24} />, title: 'Amphibious', text: 'Sea-to-shore ready' },
        { icon: <Shield size={24} />, title: 'Secure', text: 'Military encryption' },
        { icon: <Users size={24} />, title: 'Mobile', text: 'Expeditionary focus' },
        { icon: <Zap size={24} />, title: 'Rapid', text: 'Fast deployment' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Marine Corps Solutions"
                subtitle="Technology for expeditionary excellence"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Department of Defense', path: '/department' },
                    { label: 'Marine Corps Solutions' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid jk-military-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Anchor size={16} />
                                U.S. Marine Corps Technology Partner
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Expeditionary Technology for
                                <span className="highlight"> Marine Operations</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We provide rapidly deployable technology solutions designed for Marine Corps
                                expeditionary operations. From communications to combat systems, our solutions
                                enable Marines to maintain the technological edge in any environment.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Deploy Solutions
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/department" className="jk-btn jk-btn--secondary">
                                    DoD Overview
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Server size={16} />
                            Marine Corps Solutions
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Expeditionary
                            <span className="highlight"> Technology</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            Systems designed for rapid deployment and harsh conditions
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        {products.map((product, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-header">
                                    <div className="jk-solutions-services__card-icon">
                                        {product.icon}
                                    </div>
                                    <span className="jk-solutions-services__card-badge">{product.badge}</span>
                                </div>
                                <h3 className="jk-solutions-services__card-title">{product.title}</h3>
                                <p className="jk-solutions-services__card-description">{product.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {product.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-solutions-capabilities">
                <div className="jk-solutions-capabilities__container">
                    <div className="jk-solutions-capabilities__header">
                        <h2 className="jk-solutions-capabilities__title">
                            Expeditionary
                            <span className="highlight"> Capabilities</span>
                        </h2>
                    </div>

                    <div className="jk-solutions-capabilities__grid">
                        {capabilities.map((capability, index) => (
                            <div key={index} className="jk-solutions-capabilities__card">
                                <div className="jk-solutions-capabilities__card-icon">
                                    {capability.icon}
                                </div>
                                <div className="jk-solutions-capabilities__card-metric">{capability.metric}</div>
                                <h3 className="jk-solutions-capabilities__card-title">{capability.title}</h3>
                                <p className="jk-solutions-capabilities__card-text">{capability.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernMarineCorps;
